import { ResizeObserver } from '@juggle/resize-observer';
import { FC, ReactElement, ReactNode } from 'react';
import useMeasure, { RectReadOnly } from 'react-use-measure';
import { Colors } from 'src/styles';
import styled, { css } from 'styled-components';

/**
 * @deprecated
 * Don't use `<InputContainer/>` directly, but use `<InnerAddon />` instead.
 * It allows you to set the addons dynamically and returns the the size of your
 * addon, in case you need it.
 */
export const InputContainer = styled.div`
  position: relative;
`;

export const addonPadding = '1rem';

/**
 * @deprecated Don't use `<AddonRight/>` directly, but use `<InnerAddon />` instead.
 * It allows you to set the addons dynamically and returns the the size of your
 * addon, in case you need it.
 */
export const AddonRight = styled.div<{
  left?: boolean;
  showError?: boolean;
  disabled?: boolean;
}>`
  position: absolute;
  top: 0;
  height: 100%;
  padding-left: ${addonPadding};
  padding-right: ${addonPadding};
  display: flex;
  align-items: center;

  ${({ left }) =>
    left
      ? css`
          left: 0;
        `
      : css`
          right: 0;
        `}

  pointer-events: none;

  ${({ showError }) =>
    showError &&
    css`
      color: ${Colors.error};
    `}

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${Colors.inactive};
      cursor: not-allowed;
    `}
`;

type InnerAddonProps = {
  children: ({
    leftBounds,
    rightBounds,
  }: {
    leftBounds?: RectReadOnly;
    rightBounds?: RectReadOnly;
  }) => ReactNode;
  /**
   * If you need interactivity in your addon, you can wrap your custom UI with `pointer-events: initial;`.
   */
  right?: ReactElement | string;
  /**
   * If you need interactivity in your addon, you can wrap your custom UI with `pointer-events: initial;`.
   */
  left?: ReactElement | string;
  showError?: boolean;
  disabled?: boolean;
};

export const InnerAddon: FC<InnerAddonProps> = ({
  children,
  right,
  left,
  showError,
  disabled,
}) => {
  const [refLeft, leftBounds] = useMeasure({ polyfill: ResizeObserver });
  const [refRight, rightBounds] = useMeasure({ polyfill: ResizeObserver });

  if (!right && !left) return <>{children({})}</>;

  return (
    <InputContainer>
      {children({
        rightBounds: right ? rightBounds : undefined,
        leftBounds: left ? leftBounds : undefined,
      })}
      {left && (
        <AddonRight
          ref={refLeft}
          left
          showError={showError}
          disabled={disabled}
        >
          {left}
        </AddonRight>
      )}

      <AddonRight ref={refRight} showError={showError} disabled={disabled}>
        {right}
      </AddonRight>
    </InputContainer>
  );
};
